import React, { Component } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4lang_pl_PL from "@amcharts/amcharts4/lang/pl_PL";

class CollectionStatisticsChart extends Component {
  chart = null;

  componentDidMount() {
    this.renderChart();
  }

  componentDidUpdate(previousProps) {
    if (this.dataKey(previousProps) !== this.dataKey(this.props)) {
      this.disposeChart();
      this.renderChart();
    }
  }

  componentWillUnmount() {
    this.disposeChart();
  }

  dataKey(props) {
    const monthly = Array.isArray(props.monthly) ? props.monthly : [];
    return monthly
      .map(item => `${item.month}-${item.total_amount}-${item.collection_count}`)
      .join("|");
  }

  disposeChart() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }

  formatNumber(value, digits) {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (!Number.isFinite(number)) return "—";
    return number.toLocaleString("pl-PL", {
      minimumFractionDigits: digits || 0,
      maximumFractionDigits: digits || 0
    });
  }

  pdfTableRows() {
    const monthly = Array.isArray(this.props.monthly)
      ? this.props.monthly
      : [];
    return [
      [
        { text: "Miesiąc", bold: true },
        { text: "Liczba odbiorów", bold: true },
        { text: "Łączna ilość", bold: true },
        { text: "Średni odbiór", bold: true },
        { text: "Średnia temperatura", bold: true }
      ]
    ].concat(
      monthly.map(item => [
        item.month,
        String(item.collection_count),
        this.formatNumber(item.total_amount, 1),
        this.formatNumber(item.average_amount, 1),
        item.average_temperature === null
          ? "—"
          : `${this.formatNumber(item.average_temperature, 1)} °C`
      ])
    );
  }

  pdfCollectionRows() {
    const collections = Array.isArray(this.props.collections)
      ? this.props.collections.slice(0, 500)
      : [];
    return [
      [
        { text: "Data", bold: true },
        { text: "Urządzenie", bold: true },
        { text: "Ilość", bold: true },
        { text: "Temperatura", bold: true },
        { text: "Notatka", bold: true }
      ]
    ].concat(
      collections.map(item => [
        new Date(Number(item.started_at) * 1000).toLocaleString("pl-PL"),
        item.device_name || item.device_id,
        this.formatNumber(item.amount, 1),
        item.temperature === null
          ? "—"
          : `${this.formatNumber(item.temperature, 1)} °C`,
        item.note || ""
      ])
    );
  }

  exportPdf = () => {
    if (!this.chart)
      return Promise.reject(new Error("Wykres nie jest jeszcze gotowy."));
    this.chart.exporting.title =
      this.props.title || "Statystyki odbiorów medium";
    return this.chart.exporting.export("pdf", {
      pageSize: "A4",
      pageOrientation: "landscape",
      fileName: this.props.fileName || "statystyki-odbiorow",
      addURL: false
    });
  };

  renderChart() {
    const monthly = Array.isArray(this.props.monthly)
      ? this.props.monthly
      : [];
    const chart = am4core.create(
      "collection-statistics-chart",
      am4charts.XYChart
    );
    this.chart = chart;
    chart.language.locale = am4lang_pl_PL;
    chart.data = monthly.map(item => ({
      month: item.month,
      amount: Number(item.total_amount),
      count: Number(item.collection_count)
    }));

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "month";
    categoryAxis.title.text = "Miesiąc";
    categoryAxis.renderer.minGridDistance = 35;

    const amountAxis = chart.yAxes.push(new am4charts.ValueAxis());
    amountAxis.title.text = "Ilość odebranego medium";
    amountAxis.min = 0;

    const countAxis = chart.yAxes.push(new am4charts.ValueAxis());
    countAxis.title.text = "Liczba odbiorów";
    countAxis.renderer.opposite = true;
    countAxis.min = 0;
    countAxis.renderer.grid.template.disabled = true;

    const amountSeries = chart.series.push(new am4charts.ColumnSeries());
    amountSeries.name = "Łączna ilość";
    amountSeries.dataFields.categoryX = "month";
    amountSeries.dataFields.valueY = "amount";
    amountSeries.columns.template.fill = am4core.color("#1DC7EA");
    amountSeries.columns.template.stroke = am4core.color("#1DC7EA");
    amountSeries.columns.template.tooltipText =
      "{categoryX}: [bold]{valueY.formatNumber('#,###.0')}[/]";

    const countSeries = chart.series.push(new am4charts.LineSeries());
    countSeries.name = "Liczba odbiorów";
    countSeries.dataFields.categoryX = "month";
    countSeries.dataFields.valueY = "count";
    countSeries.yAxis = countAxis;
    countSeries.stroke = am4core.color("#FB404B");
    countSeries.strokeWidth = 3;
    countSeries.tooltipText = "{categoryX}: [bold]{valueY} odbiorów[/]";
    const bullet = countSeries.bullets.push(new am4charts.CircleBullet());
    bullet.circle.fill = am4core.color("#FB404B");

    chart.legend = new am4charts.Legend();
    chart.cursor = new am4charts.XYCursor();

    chart.exporting.adapter.add("pdfmakeDocument", target => {
      target.doc.content.push(
        {
          text: "Podsumowanie miesięczne",
          bold: true,
          fontSize: 14,
          margin: [0, 16, 0, 8]
        },
        {
          table: {
            headerRows: 1,
            widths: ["*", "*", "*", "*", "*"],
            body: this.pdfTableRows()
          },
          fontSize: 8
        },
        {
          text: "Zarejestrowane odbiory",
          bold: true,
          fontSize: 14,
          margin: [0, 16, 0, 8],
          pageBreak: "before"
        },
        {
          table: {
            headerRows: 1,
            widths: [85, 100, 55, 65, "*"],
            body: this.pdfCollectionRows()
          },
          fontSize: 7
        }
      );
      return target;
    });
  }

  render() {
    return (
      <div
        id="collection-statistics-chart"
        style={{ width: "100%", height: "420px" }}
      />
    );
  }
}

export default CollectionStatisticsChart;



// WEBPACK FOOTER //
// ./src/components/Charts/CollectionStatisticsChart.jsx